import { useEffect, useRef, useState } from "react";
import {
    Box, TextField, Typography, Button, Snackbar, Alert,
    CircularProgress, ThemeProvider,
} from "@mui/material";
import { Grid } from "@mui/system";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "axios";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { useLocation, useNavigate } from "react-router-dom";
import { makeGetRequestToUrl, makePostRequestToUrl } from '../commonFunc.jsx';
import config from '../../config/config.js';
import {
    c, theme, serif, useFonts,
    FieldLabel, PasswordToggle,
} from './MerchantSelfRegister.jsx'; // adjust path

// TODO: confirm with backend
const VERIFY_ENDPOINT = '/merchant/fetch_registration_documents';
const SUBMIT_DOCS_ENDPOINT = '/merchant/upload_documents';
const UPLOAD_IMAGE_ENDPOINT = '/upload_image';
const BUSINESS_TOKEN = 'ZFOGFQKCi1X8GxjkC4RV2O04dAeT19zO';
const LOGIN_ROUTE = '/signup';
const SUCCESS_ROUTE = '/merchant-docs-success';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fromServerDoc = (doc) => ({
    document_id: doc.document_id,
    document_number: doc.document_number || '',
    expiry_date: doc.expiry_date ? dayjs(doc.expiry_date).format('YYYY-MM-DD') : '',
    images: Array.isArray(doc.images) ? doc.images : [],
});

// TODO: confirm status codes with backend. Assumed: 0 pending, 1 approved, 2 rejected
const statusInfo = (doc) => {
    const hasImages = (doc.images || []).length > 0;
    switch (doc.status) {
        case 1: return { label: 'Approved', color: '#2E7D4F' };
        case 3: return { label: 'Needs re-upload', color: c.error };
        default: return hasImages
            ? { label: 'Under review', color: c.brass }
            : { label: 'Not uploaded', color: c.inkSoft };
    }
};

const isEditable = (doc) => doc.can_upload !== false;
const showExpiryField = (doc) => doc.is_doc_expired === 1 || Boolean(doc.expiry_date);

const cleanImages = (d) => (d.images || []).filter(Boolean);

// true if the merchant changed number/images compared to what the server sent
const isChanged = (value, doc) => {
    const orig = fromServerDoc(doc);
    return (
        (value.document_number || '').trim() !== (orig.document_number || '').trim() ||
        JSON.stringify(cleanImages(value)) !== JSON.stringify(cleanImages(orig))
    );
};

const buildDocPayload = (d) => {
    const item = { document_id: d.document_id, images: cleanImages(d) };
    const num = d.document_number?.trim();
    if (num) item.document_number = num;
    return item;
};

function PermissionDeniedModal() {
    return (
        <div style={styles.overlay} role="dialog" aria-modal="true" aria-labelledby="denied-title">
            <div style={styles.modal}>
                <h2 id="denied-title" style={{ margin: 0 }}>Permission Denied</h2>
                <p style={{ margin: "12px 0 0" }}>
                    You don't have permission to access this page. Please use the
                    registration link provided to you.
                </p>
            </div>
        </div>
    );
}

const styles = {
    overlay: {
      position: "fixed",
      inset: 0,
      background: "rgba(0, 0, 0, 0.6)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
    },
    modal: {
      background: "#fff",
      padding: "24px 28px",
      borderRadius: 8,
      maxWidth: 400,
      width: "90%",
      textAlign: "center",
      boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
    },
};

const MerchantAddDocs = () => {
    useFonts();
    const navigate = useNavigate();
    const serverUrl = config.serverUrl;
    const operationsUrl = config.operationsUrl;
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    // step: 'verify' | 'documents'
    const [step, setStep] = useState('verify');
    const [creds, setCreds] = useState({ email: '', password: '' });
    const [credErrors, setCredErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [approvedMessage, setApprovedMessage] = useState('');

    const [docs, setDocs] = useState([]);
    const [docValues, setDocValues] = useState([]);
    const [docErrors, setDocErrors] = useState([]);
    const [uploadingKey, setUploadingKey] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [merchant, setMerchant] = useState(null);

    const location = useLocation();
    const search = location.search.replace(/\?/g, '&').replace('&', '?');
    const params = new URLSearchParams(search);
    const queryBusToken = params.get("t");
    const isTokenMissing = !queryBusToken || !queryBusToken.trim();
    const [tokenRejected, setTokenRejected] = useState(false);

    const notify = (message, severity = 'error') => setSnackbar({ open: true, message, severity });

    // show a message passed in from the previous page (e.g. registration 410)
    useEffect(() => {
        const n = location.state?.notification;
        if (n?.message) {
            notify(n.message, n.severity || 'error');
            // clear it so refresh / back doesn't show it again
            navigate(location.pathname + location.search, { replace: true, state: null });
        }
    }, []);

    // ---------- step 1: verify ----------
    const setCred = (field) => (e) => {
        setCreds((p) => ({ ...p, [field]: e.target.value }));
        setCredErrors((p) => (p[field] ? { ...p, [field]: undefined } : p));
        setApprovedMessage('');
    };

    const verify = async () => {
        if (verifying) return;
        const next = {};
        if (!creds.email.trim()) next.email = 'Enter your registered email';
        else if (!EMAIL_REGEX.test(creds.email.trim())) next.email = 'This email address looks invalid';
        if (!creds.password) next.password = 'Enter your password';
        setCredErrors(next);
        if (Object.keys(next).length) return;

        setVerifying(true);
        setApprovedMessage('');
        try {
            const response = await makePostRequestToUrl(serverUrl + VERIFY_ENDPOINT, {
                business_token: queryBusToken,
                email: creds.email.trim(),
                password: creds.password,
            });

            if (response.flag == 401) {
                setTokenRejected(true);
                return;
            }

            if (response?.flag == 410) {
                setApprovedMessage(response.message || 'Your account is already approved, please log in to the merchant panel');
                return;
            }
            if (response?.flag != 200) {
                notify(response?.message || 'We couldn\u2019t verify these details');
                return;
            }
            
            const list = Array.isArray(response?.data?.documents) ? response.data.documents : [];
            
            if (!mountedRef.current) return;
            setMerchant(response.data);
            setDocs(list);
            setDocValues(list.map(fromServerDoc));
            setDocErrors([]);
            setStep('documents');
        } catch (error) {
            console.error('Error verifying merchant:', error);
            notify(error?.message || 'Something went wrong. Please try again.');
        } finally {
            if (mountedRef.current) setVerifying(false);
        }
    };

    // ---------- step 2: documents ----------
    const updateDoc = (index, field, value) =>
        setDocValues((prev) => prev.map((d, i) => (i === index ? { ...d, [field]: value } : d)));

    const clearDocError = (index, field) =>
        setDocErrors((prev) => {
            if (!prev[index]?.[field]) return prev;
            const next = [...prev];
            next[index] = { ...next[index] };
            delete next[index][field];
            return next;
        });

    const handleFileChange = (index, imgIndex) => async (event) => {
        const input = event.target;
        const file = input.files?.[0];
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            notify('Please choose an image file');
            input.value = '';
            return;
        }
        setUploadingKey(`${index}-${imgIndex}`);
        try {
            const formData = new FormData();
            formData.append('image', file);
            const response = await axios.post(operationsUrl + UPLOAD_IMAGE_ENDPOINT, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            const s3Url = response.data?.url;
            if (!s3Url) throw new Error('No URL returned');
            setDocValues((prev) => prev.map((d, i) => {
                if (i !== index) return d;
                const images = [...(d.images || [])];
                images[imgIndex] = s3Url;
                return { ...d, images };
            }));
            clearDocError(index, 'image');
        } catch (error) {
            console.error('Error uploading image:', error);
            notify('The upload didn\u2019t go through. Please try again.');
        } finally {
            setUploadingKey('');
            input.value = '';
        }
    };

    const validateDocuments = () => {
        const next = docs.map((doc, i) => {
            const err = {};
            if (!isEditable(doc)) return err;
    
            const d = docValues[i] || {};
            const images = cleanImages(d);
            const started = Boolean(d.document_number?.trim() || images.length);
            if (doc.is_required !== 1 && !started) return err;
    
            if (doc.is_doc_number_required === 1 && !d.document_number?.trim()) err.documentNumber = 'Enter the document number';
            const needed = Number(doc.no_of_images) || 1;
            if (images.length < needed) err.image = needed > 1 ? `Upload all ${needed} images` : 'Upload an image of this document';
            return err;
        });
        setDocErrors(next);
        return next.every((e) => Object.keys(e).length === 0);
    };

    const submitDocs = async () => {
        if (submitting) return;
        if (uploadingKey) return notify('Please wait for the uploads to finish');
        if (!validateDocuments()) return notify('Some details need your attention');
    
        const merchant_docs = docValues
            .map((d, i) => ({ d, doc: docs[i] }))
            .filter(({ d, doc }) => isEditable(doc) && isChanged(d, doc))
            .map(({ d }) => buildDocPayload(d));
    
        if (merchant_docs.length === 0) {
            return notify('Upload or change a document before submitting');
        }
    
        setSubmitting(true);
        try {
            const response = await makePostRequestToUrl(operationsUrl + SUBMIT_DOCS_ENDPOINT, {
                business_token: queryBusToken,
                email: creds.email.trim(),
                password: creds.password,
                merchant_docs,
            });
    
            if (response?.flag === 200) {
                notify(response.message || 'Documents sent for review', 'success');
                navigate(SUCCESS_ROUTE);
            } else {
                notify(response?.message || 'We couldn\u2019t submit your documents');
            }
        } catch (error) {
            console.error('Error submitting documents:', error);
            notify(error?.message || 'Something went wrong. Please try again.');
        } finally {
            if (mountedRef.current) setSubmitting(false);
        }
    };

    const handleSnackbarClose = (e, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar((p) => ({ ...p, open: false }));
    };

    // ---------- render ----------
    const cardSx = {
        bgcolor: c.surface, border: `1px solid ${c.line}`, borderRadius: '10px',
        p: { xs: 3, sm: 4 },
        boxShadow: '0 1px 0 rgba(20,33,61,0.03), 0 12px 32px -20px rgba(20,33,61,0.18)',
    };

    return (
        <>
        {(isTokenMissing || tokenRejected) && <PermissionDeniedModal />}
        {!isTokenMissing && !tokenRejected && (

        <ThemeProvider theme={theme}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box sx={{ minHeight: '100dvh', bgcolor: c.bone, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', px: { xs: 2.5, sm: 5 }, py: { xs: 4, md: 7 } }}>
                    <Box sx={{ width: '100%', maxWidth: 640 }}>

                        <Typography sx={{ fontFamily: serif, fontSize: '28px', fontWeight: 500, color: c.ink, letterSpacing: '-0.3px', lineHeight: 1.2 }}>
                            {step === 'verify' ? 'Welcome back' : 'Add your documents'}
                        </Typography>
                        <Typography sx={{ fontSize: '14.5px', color: c.inkSoft, mt: 1, mb: 4 }}>
                            {step === 'verify'
                                ? 'Sign in with the email and password you registered with.'
                                : `${merchant?.merchant_name || ''} · ${creds.email.trim()}`}
                        </Typography>

                        {step === 'verify' && (
                            <Box sx={cardSx}>
                                <Grid container spacing={2.5}>
                                    <Grid size={12}>
                                        <FieldLabel required>Email</FieldLabel>
                                        <TextField
                                            placeholder="you@business.com" fullWidth type="email"
                                            value={creds.email} onChange={setCred('email')}
                                            error={Boolean(credErrors.email)} helperText={credErrors.email}
                                            autoComplete="email"
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <FieldLabel required>Password</FieldLabel>
                                        <TextField
                                            placeholder="Your password" fullWidth
                                            type={showPassword ? 'text' : 'password'}
                                            value={creds.password} onChange={setCred('password')}
                                            onKeyDown={(e) => { if (e.key === 'Enter') verify(); }}
                                            error={Boolean(credErrors.password)} helperText={credErrors.password}
                                            autoComplete="current-password"
                                            InputProps={{ endAdornment: <PasswordToggle shown={showPassword} onToggle={() => setShowPassword((s) => !s)} /> }}
                                        />
                                    </Grid>

                                    {approvedMessage && (
                                        <Grid size={12}>
                                            <Alert
                                                severity="success"
                                                sx={{ borderRadius: '8px', alignItems: 'center' }}
                                                action={
                                                    <Button color="inherit" size="small" sx={{ textTransform: 'none', fontWeight: 600 }} onClick={() => navigate(LOGIN_ROUTE)}>
                                                        Go to login
                                                    </Button>
                                                }
                                            >
                                                {approvedMessage}
                                            </Alert>
                                        </Grid>
                                    )}
                                </Grid>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 4, flexWrap: 'wrap', gap: 2 }}>
                                    <Typography
                                        onClick={() => navigate(-1)}
                                        sx={{ fontSize: '13.5px', color: c.inkSoft, textDecoration: 'underline', cursor: 'pointer' }}
                                    >
                                        Back to registration
                                    </Typography>
                                    <Button
                                        variant="contained" disableElevation onClick={verify} disabled={verifying}
                                        endIcon={!verifying && <ArrowForwardIcon sx={{ fontSize: '18px !important' }} />}
                                        sx={{
                                            bgcolor: c.navy, color: '#fff', textTransform: 'none', fontWeight: 500, fontSize: '15px',
                                            borderRadius: '6px', minWidth: 180, height: 48, px: 3,
                                            '&:hover': { bgcolor: c.navyDeep },
                                            '&.Mui-disabled': { bgcolor: c.navy, color: '#fff', opacity: 0.7 },
                                        }}
                                    >
                                        {verifying ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Continue'}
                                    </Button>
                                </Box>
                            </Box>
                        )}

                        {step === 'documents' && (
                            <Box sx={cardSx}>
                                {docs.length === 0 ? (
                                    <Typography sx={{ fontSize: '14px', color: c.inkSoft }}>No documents are needed right now.</Typography>
                                ) : (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        {docs.map((doc, index) => {
                                            const values = docValues[index] || {};
                                            const err = docErrors[index] || {};
                                            const hasError = Boolean(err.documentNumber || err.expiryDate || err.image);
                                            const isRequired = doc.is_required === 1;
                                            const imageCount = Number(doc.no_of_images) || 1;
                                            const editable = isEditable(doc);
                                            const status = statusInfo(doc);
                                            const showExpiry = showExpiryField(doc);
                                            const showNumber = doc.is_doc_number_required === 1 || Boolean(doc.document_number);

                                            return (
                                                <Box key={doc.document_id} sx={{
                                                    border: `1px solid ${hasError ? c.error : c.line}`,
                                                    borderRadius: '8px', bgcolor: c.bone, p: { xs: 2, sm: 2.5 },
                                                }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: doc.reason ? 0.5 : 2 }}>
                                                            <Typography sx={{ fontSize: '15px', fontWeight: 600, color: c.ink }}>
                                                                {doc.document_name}
                                                                {isRequired && <Box component="span" sx={{ color: c.brass, ml: '3px' }}>*</Box>}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: status.color }}>{status.label}</Typography>
                                                        </Box>
                                                        {doc.reason && (
                                                            <Typography sx={{ fontSize: '13px', color: c.error, mb: 2 }}>{doc.reason}</Typography>
                                                        )}

                                                    <Grid container spacing={2}>
                                                        {showNumber && (
                                                            <Grid size={{ xs: 12, sm: showExpiry ? 6 : 12 }}>
                                                                <FieldLabel required={isRequired}>Document number</FieldLabel>
                                                                <TextField disabled={!editable}
                                                                    fullWidth placeholder="As printed on the document"
                                                                    value={values.document_number || ''}
                                                                    onChange={(e) => { updateDoc(index, 'document_number', e.target.value); clearDocError(index, 'documentNumber'); }}
                                                                    error={Boolean(err.documentNumber)} helperText={err.documentNumber}
                                                                />
                                                            </Grid>
                                                        )}

                                                        {/* {showExpiry && (
                                                            <Grid size={{ xs: 12, sm: showNumber ? 6 : 12 }}>
                                                                <FieldLabel required={isRequired}>Expiry date</FieldLabel>
                                                                <DatePicker disabled={!editable}
                                                                    disablePast format="DD/MM/YYYY"
                                                                    value={values.expiry_date ? dayjs(values.expiry_date) : null}
                                                                    onChange={(v) => {
                                                                        const ok = v && v.isValid();
                                                                        updateDoc(index, 'expiry_date', ok ? v.format('YYYY-MM-DD') : '');
                                                                        if (ok) clearDocError(index, 'expiryDate');
                                                                    }}
                                                                    slotProps={{ textField: { fullWidth: true, placeholder: 'DD/MM/YYYY', error: Boolean(err.expiryDate), helperText: err.expiryDate } }}
                                                                />
                                                            </Grid>
                                                        )} */}

                                                        <Grid size={12}>
                                                            <FieldLabel required={isRequired} hint={imageCount > 1 ? `${imageCount} images, e.g. front and back` : undefined}>
                                                                {imageCount > 1 ? 'Images' : 'Image'}
                                                            </FieldLabel>
                                                            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                                                {Array.from({ length: imageCount }).map((_, imgIndex) => {
                                                                    const inputId = `merchant-add-${doc.document_id}-${imgIndex}`;
                                                                    const src = values.images?.[imgIndex];
                                                                    const busy = uploadingKey === `${index}-${imgIndex}`;
                                                                    return (
                                                                        <Box key={imgIndex} sx={{ width: 140 }}>
                                                                            <input
                                                                                id={inputId} type="file" accept="image/*"
                                                                                capture={doc.is_gallery_restricted === 1 ? 'environment' : undefined}
                                                                                style={{ display: 'none' }}
                                                                                onChange={handleFileChange(index, imgIndex)}
                                                                                disabled={busy || !editable}
                                                                            />
                                                                            <Box component="label" htmlFor={editable ? inputId : undefined} sx={{
                                                                                height: 110, borderRadius: '8px', cursor: !editable || busy ? 'default' : 'pointer',
                                                                                border: `1px dashed ${err.image && !src ? c.error : src ? c.brass : c.lineStrong}`,
                                                                                bgcolor: c.surface, overflow: 'hidden', position: 'relative',
                                                                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '6px',
                                                                                transition: 'border-color 160ms ease',
                                                                                '&:hover': { borderColor: busy || !editable ? undefined : c.navy },
                                                                            }}>
                                                                                                                                                            {busy ? (
                                                                                    <CircularProgress size={20} sx={{ color: c.navy }} />
                                                                                ) : src ? (
                                                                                    <>
                                                                                        <Box component="img" src={src} alt={`${doc.document_name} ${imgIndex + 1}`} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                                                        {editable && (
                                                                                            <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, py: '4px', bgcolor: 'rgba(11,31,58,0.72)', color: '#fff', fontSize: '11.5px', textAlign: 'center' }}>
                                                                                                Replace
                                                                                            </Box>
                                                                                        )}
                                                                                    </>
                                                                                ) : (
                                                                                    <>
                                                                                        <FileUploadOutlinedIcon sx={{ fontSize: 22, color: c.inkSoft }} />
                                                                                        <Typography sx={{ fontSize: '12.5px', color: c.inkSoft }}>
                                                                                            {imageCount > 1 ? `Image ${imgIndex + 1}` : 'Upload image'}
                                                                                        </Typography>
                                                                                    </>
                                                                                )}
                                                                            </Box>
                                                                        </Box>
                                                                    );
                                                                })}
                                                            </Box>
                                                            {err.image && <Typography sx={{ color: c.error, fontSize: '12.5px', mt: '6px' }}>{err.image}</Typography>}
                                                        </Grid>
                                                    </Grid>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}

                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4 }}>
                                    <Button
                                        variant="contained" disableElevation onClick={submitDocs}
                                        disabled={submitting || Boolean(uploadingKey) || !docs.some(isEditable)}
                                        endIcon={!submitting && <ArrowForwardIcon sx={{ fontSize: '18px !important' }} />}
                                        sx={{
                                            bgcolor: c.navy, color: '#fff', textTransform: 'none', fontWeight: 500, fontSize: '15px',
                                            borderRadius: '6px', minWidth: 200, height: 48, px: 3,
                                            '&:hover': { bgcolor: c.navyDeep },
                                            '&.Mui-disabled': { bgcolor: c.navy, color: '#fff', opacity: 0.7 },
                                        }}
                                    >
                                        {submitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Submit documents'}
                                    </Button>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </Box>

                <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleSnackbarClose} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                    <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%', borderRadius: '8px' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </LocalizationProvider>
        </ThemeProvider>
        )}
        </>
    );
};

export default MerchantAddDocs;